import { useCallback, useEffect, useState } from "react";
import { Loader2, MonitorSmartphone, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Kpi, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

export const LoginAlerts = ({ canRead, canWrite }) => {
  const [mine, setMine] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const own = await api.get("/auth/devices");
    setMine(own.data);
    if (canRead) {
      try {
        const all = await api.get("/admin/login-alerts");
        setTenant(all.data);
      } catch { setTenant(null); }
    }
  }, [canRead]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const save = async (next) => {
    setBusy(true);
    try {
      const res = await api.put("/admin/login-alerts", next);
      setTenant(res.data);
      toast.success("Sign-in alerts updated");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!mine) return null;

  return (
    <div className="space-y-4">
      {tenant && (
        <Panel testId="panel-login-alerts" kicker="Security"
               title="Tell people when their account is used from somewhere new"
               actions={canWrite && (
                 <div className="flex items-center gap-3">
                   <Select value={tenant.scope} disabled={busy}
                           onValueChange={(scope) => save({ enabled: tenant.enabled, scope })}>
                     <SelectTrigger className="h-9 w-[210px]" data-testid="login-alert-scope">
                       <SelectValue /></SelectTrigger>
                     <SelectContent>
                       {Object.entries(tenant.scopes).map(([key, label]) => (
                         <SelectItem key={key} value={key}>{label}</SelectItem>
                       ))}
                     </SelectContent>
                   </Select>
                   <Switch checked={tenant.enabled} disabled={busy}
                           data-testid="login-alert-enabled"
                           onCheckedChange={(v) => save({ enabled: v, scope: tenant.scope })} />
                   {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                 </div>
               )}>
          <p className="px-4 pt-3 text-[12px] leading-relaxed text-muted-foreground">
            A device is remembered by the browser it signed in from and the network it came from.
            The first device on an account is remembered quietly; every one after that emails the
            account holder. {tenant.enabled
              ? `Watching: ${tenant.scopes[tenant.scope].toLowerCase()}.`
              : "Alerts are switched off, so nothing is being sent."}
          </p>
          <div className="grid grid-cols-2 gap-3 px-4 pt-3 lg:grid-cols-3">
            <Kpi testId="alerts-raised" label="Alerts raised" value={tenant.alerts.length}
                 accent="amber" icon={ShieldAlert} sub="most recent 60" />
            <Kpi testId="devices-seen" label="Devices remembered" value={tenant.devices_seen}
                 icon={MonitorSmartphone} sub="across the charity" />
            <Kpi testId="alerts-mine" label="On your own account" value={mine.alerts.length}
                 sub={`${mine.devices.length} device(s) remembered`} />
          </div>
          {tenant.alerts.length === 0 ? (
            <p className="px-4 py-4 text-[12.5px] text-muted-foreground"
               data-testid="login-alerts-empty">
              Nobody has signed in from an unfamiliar device since this was switched on.
            </p>
          ) : (
            <Table testId="login-alerts-table"
                   head={<><Th>Who</Th><Th>Device</Th><Th>From</Th><Th>When</Th></>}>
              {tenant.alerts.map((a) => (
                <tr key={`${a.email}-${a.at}`} className="row-hover"
                    data-testid={`login-alert-${a.email}`}>
                  <Td>
                    <div className="text-[12.5px] text-foreground">{a.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{a.email}</div>
                  </Td>
                  <Td className="text-[11.5px] text-muted-foreground">{a.label}</Td>
                  <Td className="font-mono text-[11px]">{a.ip}</Td>
                  <Td className="font-mono text-[11px]">{dateTime(a.at)}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      )}

      <Panel testId="panel-my-devices" kicker="Your account"
             title="Where you have signed in from">
        <Table testId="my-devices-table"
               head={<><Th>Device</Th><Th>Network</Th><Th>Sign-ins</Th><Th>First seen</Th>
                 <Th>Last seen</Th></>}>
          {mine.devices.length === 0 && (
            <tr><Td colSpan={5} className="text-[12.5px] text-muted-foreground">
              Nothing remembered yet.</Td></tr>
          )}
          {mine.devices.map((d) => (
            <tr key={d.fingerprint} className="row-hover"
                data-testid={`my-device-${d.fingerprint}`}>
              <Td>
                <span className="text-[12.5px] text-foreground">{d.label}</span>
                {d.first_ever && (
                  <Chip className="ml-2 border-border bg-muted text-muted-foreground">
                    the one you started on
                  </Chip>
                )}
              </Td>
              <Td className="font-mono text-[11px]">{d.last_ip}</Td>
              <Td className="font-mono text-[11px]">{d.sign_ins}</Td>
              <Td className="font-mono text-[11px]">{dateTime(d.first_seen)}</Td>
              <Td className="font-mono text-[11px]">{dateTime(d.last_seen)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
};
